import React from "react";
import { FrontColors } from "../../../../models/FrontColors";
import { FrontSpacing } from "../../../../models/FrontSpacing";
import { Padding } from "../../../BuildingBlocks/Padding";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { TextColor } from "../../../BuildingBlocks/TextColor";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { WeightMedium } from "../../../WeightMedium/WeightMedium";
import { FrontCardTitle } from "../../Simple/FrontCardTitle/FrontCardTitle";
import { FrontIcon } from "../../Simple/FrontIcon/FrontIcon";
import FrontParagraphRegular from "../../Simple/FrontParagraphRegular/FrontParagraphRegular";
import { FrontQuestionFormCard } from "../FrontQuestionFormCard/FrontQuestionFormCard";
import { TUserCaseUserType } from "../../../../../../domain/codecs/UserCaseUserType";
import { deriveOnboardingRoleAudience } from "../../../../../../domain/onboardingComplete/onboardingRoleAudience";
import { composeOnboardingCompleteContent, TCopyLine } from "../../../../../../domain/onboardingComplete/onboardingCompleteContent";
import { TOnboardingJourney } from "../../../../../../domain/onboardingComplete/onboardingJourney";
import { TOwnershipStatus } from "../../../../../../domain/codecs/UserCaseUserType";

type TOnboardingCompleteRundown = {
    journey: TOnboardingJourney;
    session_user_ownership_status: TOwnershipStatus;
    case_handler_first_name: string | null;
    others_have_incomplete_steps: boolean;
};

type TFrontOnboardingCompleteCardProps = {
    rundown: TOnboardingCompleteRundown;
    userType: TUserCaseUserType;
};

const renderCopyLine = (line: TCopyLine, onDarkBackground: boolean = false): JSX.Element => {
    const runs = (
        <WeightMedium>
            {line.map((run, index) =>
                run.bold
                    ? <WeightBold key={index}>{run.text}</WeightBold>
                    : <React.Fragment key={index}>{run.text}</React.Fragment>
            )}
        </WeightMedium>
    );
    return (
        <FrontParagraphRegular>
            {onDarkBackground
                ? <TextColor color={FrontColors.NEUTRAL_18}>{runs}</TextColor>
                : runs}
        </FrontParagraphRegular>
    );
};

export const FrontOnboardingCompleteCard = (props: TFrontOnboardingCompleteCardProps): JSX.Element => {
    const content = composeOnboardingCompleteContent({
        journey: props.rundown.journey,
        roleAudience: deriveOnboardingRoleAudience(props.userType),
        ownershipStatus: props.rundown.session_user_ownership_status,
        caseHandlerFirstName: props.rundown.case_handler_first_name,
        othersHaveIncompleteSteps: props.rundown.others_have_incomplete_steps,
    });

    return (
        <SpacingColumn spacing={FrontSpacing.LARGE_2}>

            {/* GREEN SUCCESS HEADER — celebration beat only (title + lead-in + tick) */}
            <div className="front-onboarding-complete-card front-onboarding-info-card front-onboarding-info-card--green">
                <Padding type="right" spacing={FrontSpacing.MEDIUM_2}>
                    <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                        <FrontCardTitle>
                            <TextColor color={FrontColors.NEUTRAL_18}>
                                <WeightBold>{content.title}</WeightBold>
                            </TextColor>
                        </FrontCardTitle>
                        {renderCopyLine(content.leadIn, true)}
                    </SpacingColumn>
                </Padding>
                <div className="front-onboarding-info-card__icon">
                    <FrontIcon
                        iconName="done"
                        colour="neutral-18"
                        size="large"
                    />
                </div>
            </div>

            {/* WHITE RUNDOWN CARD — same shell as the agreed-terms step card */}
            <FrontQuestionFormCard title={content.whatHappensNextHeading}>
                <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>

                    {/* ONBOARDING TEAM — intro context for the card */}
                    <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                        {content.teamBlock.map((line, index) => (
                            <div key={index}>{renderCopyLine(line)}</div>
                        ))}
                    </SpacingColumn>

                    {/* NUMBERED JOURNEY STEPS */}
                    <ol className="front-onboarding-complete-card__steps">
                        {content.journeySteps.map((line, index) => (
                            <li key={index} className="front-onboarding-complete-card__step">
                                {renderCopyLine(line)}
                            </li>
                        ))}
                    </ol>

                    {/* OWNERSHIP LINE (transfers only) */}
                    {content.ownershipLine !== null && renderCopyLine(content.ownershipLine)}

                    {/* WAITING ON OTHERS */}
                    {content.waitingOnOthersLine !== null && renderCopyLine(content.waitingOnOthersLine)}

                    {/* ROLE BLOCK */}
                    {content.roleBlock !== null && renderCopyLine(content.roleBlock)}

                    {/* OUTRO */}
                    {renderCopyLine(content.outro)}

                </SpacingColumn>
            </FrontQuestionFormCard>

        </SpacingColumn>
    );
};
